import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Spot } from 'domain/Spot';
import { Shift } from 'domain/Shift';
import { createCorporatePage, drawSpotInformation, drawObservations, drawThankYouMessage, drawFooter } from './PdfUtils';

interface GeneratedSpotPDF {
  doc: jsPDF;
  fileName: string;
}

const isHolidayOrSunday = (
  date: moment.Moment,
  holidays: Date[]
) => {
  if (date.day() === 0) {
    return true; // Domingo
  }

  return holidays.some(h =>
    moment(h).format('YYYY-MM-DD') === date.format('YYYY-MM-DD')
  );
};

export const exportWeeklyGridPDFBySpot = (
  spotList: Spot[],
  spotIdToShiftListMap: Map<number, Shift[]>,
  weekStart: Date,
  holidays: Date[]
) => {

  const doc = new jsPDF('landscape');

  const startOfWeek = moment(weekStart).startOf('week');
  const days = Array.from({ length: 7 }).map((_, i) =>
    startOfWeek.clone().add(i, 'days')
  );

  // 🔹 HEADER
  const tableHead = [
    ['Cliente', ...days.map(d => d.format('ddd DD/MM'))]
  ];

  const tableBody: any[] = [];

  spotList.forEach(spot => {

    const shifts = spotIdToShiftListMap.get(spot.id!) || [];

    const row: string[] = [spot.name];

    days.forEach(day => {

      const shiftsForDay = shifts.filter(s =>
        moment(s.startDateTime).isSame(day, 'day')
      );

      if (shiftsForDay.length === 0) {
        row.push('-');
      } else {

        const contenido = shiftsForDay.map(shift => {

          const lineas = [
            shift.employee?.name || 'Sin asignar',
            `${moment(shift.startDateTime).format('HH:mm')} - ${moment(shift.endDateTime).format('HH:mm')}`,
            shift.rotationEmployee?.name
              ? `${shift.rotationEmployee.name}`
              : ''
          ];

          return lineas
            .filter(l => l && l.toString().trim() !== '')
            .join('\n');

        }).join('\n\n'); // separación entre turnos

        row.push(contenido);
      }

    });

    tableBody.push(row);
  });

  // 🔹 TÍTULO
  doc.setFontSize(16);
  doc.text('Programación semanal por puesto', 14, 15);

  doc.setFontSize(10);
  doc.text(
    `Semana: ${startOfWeek.format('DD/MM/YYYY')} - ${startOfWeek.clone().endOf('week').format('DD/MM/YYYY')}`,
    14,
    22
  );

  // 🔹 TABLA
  autoTable(doc, {
    head: tableHead,
    body: tableBody,
    startY: 30,
    styles: {
      fontSize: 7,
      overflow: 'linebreak',
      valign: 'top'
    },
    columnStyles: {
      0: { cellWidth: 35 }, // spot
      1: { cellWidth: 34 },
      2: { cellWidth: 34 },
      3: { cellWidth: 34 },
      4: { cellWidth: 34 },
      5: { cellWidth: 34 },
      6: { cellWidth: 34 },
      7: { cellWidth: 34 },
    },
    headStyles: {
      fillColor: [64, 132, 199],
      textColor: 255,
    },
    didParseCell: (data) => {

      // columna 0 = Puesto
      if (data.column.index === 0) {
        return;
      }

      const day = days[data.column.index - 1];

      if (!isHolidayOrSunday(day, holidays)) {
        return;
      }

      // Encabezado
      if (data.section === 'head') {
        data.cell.styles.fillColor = [192, 57, 43];
        data.cell.styles.textColor = 255;
        data.cell.styles.fontStyle = 'bold';
      }

      // Cuerpo
      if (data.section === 'body') {
        data.cell.styles.fillColor = [255, 235, 238];
      }

    },
  });

  doc.save(`programacion_spots_${startOfWeek.format('YYYY-MM-DD')}.pdf`);
};

export const exportWeeklyGridPDFByEmployee = (
  spotList: Spot[],
  spotIdToShiftListMap: Map<number, Shift[]>,
  weekStart: Date,
  holidays: Date[]
) => {

  const doc = new jsPDF('landscape');

  const startOfWeek = moment(weekStart).startOf('week');
  const days = Array.from({ length: 7 }).map((_, i) =>
    startOfWeek.clone().add(i, 'days')
  );

  // 🔥 AGRUPAR EMPLEADOS
  const employeeMap: Map<number, { name: string, shifts: Shift[] }> = new Map();

  spotList.forEach(spot => {
    const shifts = spotIdToShiftListMap.get(spot.id!) || [];

    shifts.forEach(shift => {
      const empId = shift.employee?.id ?? -1;
      const empName = shift.employee?.name ?? 'Sin asignar';

      if (!employeeMap.has(empId)) {
        employeeMap.set(empId, {
          name: empName,
          shifts: []
        });
      }

      employeeMap.get(empId)!.shifts.push(shift);
    });
  });

  // 🔥 HEADERS
  const tableHead = [
    ['Empleado', ...days.map(d => d.format('ddd DD/MM'))]
  ];

  const tableBody: any[] = [];

  employeeMap.forEach(emp => {

    const row: string[] = [emp.name];

    days.forEach(day => {

      const shiftsForDay = emp.shifts.filter(s =>
        moment(s.startDateTime).isSame(day, 'day')
      );

      if (shiftsForDay.length === 0) {
        row.push('-');
      } else {

        const contenido = shiftsForDay.map(shift => {

          const lineas = [
            shift.spot?.name,
            `${moment(shift.startDateTime).format('HH:mm')} - ${moment(shift.endDateTime).format('HH:mm')}`,
            shift.spot?.address
              ? `${shift.spot.address}`
              : shift.spot?.address,
              shift.spot?.city
              ? `${shift.spot.city}`
              : shift.spot?.city,
            shift.spot?.observations
          ];

          // 🔥 quitar null, undefined, ''
          return lineas
            .filter(l => l && l.toString().trim() !== '')
            .join('\n');

        }).join('\n\n'); // separación entre turnos

        row.push(contenido);
      }

    });

    tableBody.push(row);
  });

  // 🔹 HEADER PDF
  doc.setFontSize(16);
  doc.text('Programación semanal por empleado', 14, 15);

  doc.setFontSize(10);
  doc.text(
    `Semana: ${startOfWeek.format('DD/MM/YYYY')} - ${startOfWeek.clone().endOf('week').format('DD/MM/YYYY')}`,
    14,
    22
  );

  // 🔹 TABLA
  autoTable(doc, {
    head: tableHead,
    body: tableBody,
    startY: 30,
    styles: {
      fontSize: 7,
      cellWidth: 'wrap',
      overflow: 'linebreak',
      valign: 'top'
    },
    columnStyles: {
      0: { cellWidth: 35 }, // empleado
      1: { cellWidth: 34 },
      2: { cellWidth: 34 },
      3: { cellWidth: 34 },
      4: { cellWidth: 34 },
      5: { cellWidth: 34 },
      6: { cellWidth: 34 },
      7: { cellWidth: 34 },
    },
    headStyles: {
      fillColor: [64, 132, 199],
      textColor: 255,
    },
    didParseCell: (data) => {

      // columna 0 = Puesto
      if (data.column.index === 0) {
        return;
      }

      const day = days[data.column.index - 1];

      if (!isHolidayOrSunday(day, holidays)) {
        return;
      }

      // Encabezado
      if (data.section === 'head') {
        data.cell.styles.fillColor = [192, 57, 43];
        data.cell.styles.textColor = 255;
        data.cell.styles.fontStyle = 'bold';
      }

      // Cuerpo
      if (data.section === 'body') {
        data.cell.styles.fillColor = [255, 235, 238];
      }

    },
  });

  doc.save(`programacion_empleados_${startOfWeek.format('YYYY-MM-DD')}.pdf`);
};


export const exportSingleEmployeePDF = async (
  employee: { name: string; shifts: Shift[] },
  weekStart: Date,
  holidays: Date[]
) => {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  const startOfWeek = moment(weekStart).startOf('week');
  const endOfWeek = startOfWeek.clone().endOf('week');

  const weekLabel =
    `${startOfWeek.format('YYYY-MM-DD')}_a_${endOfWeek.format('YYYY-MM-DD')}`;

  const days = Array.from({ length: 7 }, (_, i) =>
    startOfWeek.clone().add(i, 'days')
  );

  // --------------------------------------------------
  // COLORES Y CONFIGURACIÓN
  // --------------------------------------------------

  type ShiftType = 'morning' | 'afternoon' | 'full';

  type RGB = [number, number, number];

  interface ShiftCardData {
    spot: string;
    time: string;
    location: string;
    observations: string;
    type: ShiftType;
  }

  const COLORS: Record<
    ShiftType,
    { background: RGB; border: RGB; text: RGB; label: string }
  > = {
    morning: {
      background: [224, 242, 254],
      border: [125, 211, 252],
      text: [7, 89, 133],
      label: 'MAÑANA',
    },
    afternoon: {
      background: [255, 240, 214],
      border: [253, 186, 116],
      text: [146, 64, 14],
      label: 'TARDE',
    },
    full: {
      background: [237, 233, 254],
      border: [196, 181, 253],
      text: [91, 33, 182],
      label: 'JORNADA COMPLETA',
    },
  };

  const PAGE_MARGIN = 10;
  const FIRST_COLUMN_WIDTH = 38;
  const CARD_PADDING = 1.5;
  const CARD_GAP = 2;
  const CARD_RADIUS = 2;

  const pageWidth = doc.internal.pageSize.getWidth();
  const dayColumnWidth =
    (pageWidth - PAGE_MARGIN * 2 - FIRST_COLUMN_WIDTH) / 7;

  const cardWidth = dayColumnWidth - 3;
  const textWidth = cardWidth - CARD_PADDING * 2;

  // --------------------------------------------------
  // CLASIFICAR TURNOS
  // --------------------------------------------------

  const getShiftType = (shift: Shift): ShiftType => {
    const start = moment(shift.startDateTime);
    const end = moment(shift.endDateTime);

    const noon = start.clone().startOf('day').hour(12);

    // Turnos que cruzan de la mañana a la tarde,
    // incluidos los que terminan al día siguiente.
    if (start.isBefore(noon) && end.isAfter(noon)) {
      return 'full';
    }

    if (start.isBefore(noon)) {
      return 'morning';
    }

    return 'afternoon';
  };

  // --------------------------------------------------
  // PREPARAR TURNOS POR DÍA
  // --------------------------------------------------

  const shiftsByDay: ShiftCardData[][] = days.map(day => {
    return employee.shifts
      .filter(shift =>
        moment(shift.startDateTime).isSame(day, 'day')
      )
      .sort((a, b) =>
        moment(a.startDateTime).valueOf() -
        moment(b.startDateTime).valueOf()
      )
      .map(shift => {
        const spot = shift.spot?.name ?? '';

        const time =
          `${moment(shift.startDateTime).format('h:mm A')} - ` +
          `${moment(shift.endDateTime).format('h:mm A')}`;

        const location = [
          shift.spot?.address,
          shift.spot?.city,
        ]
          .filter(Boolean)
          .join(' ');

        const observations =
          shift.spot?.observations ?? '';

        return {
          spot,
          time,
          location,
          observations,
          type: getShiftType(shift),
        };
      });
  });

  // --------------------------------------------------
  // CALCULAR ALTURA DE CADA RECUADRO
  // --------------------------------------------------

  const getCardHeight = (card: ShiftCardData): number => {
    let height = CARD_PADDING * 2;

    // Etiqueta MAÑANA / TARDE / JORNADA COMPLETA
    height += 4;

    // Horario
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);

    const timeLines = doc.splitTextToSize(
      card.time,
      textWidth
    );

    height += timeLines.length * 3.7 + 1;

    // Nombre del puesto
    if (card.spot) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);

      const lines = doc.splitTextToSize(
        card.spot,
        textWidth
      );

      height += lines.length * 3 + 1;
    }

    // Dirección y ciudad
    if (card.location) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);

      const lines = doc.splitTextToSize(
        card.location,
        textWidth
      );

      height += lines.length * 2.8 + 1;
    }

    // Observaciones
    if (card.observations) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);

      const lines = doc.splitTextToSize(
        String(card.observations),
        textWidth
      );

      height += lines.length * 2.8 + 1;
    }

    return height;
  };

  const getDayHeight = (cards: ShiftCardData[]): number => {
    if (cards.length === 0) {
      return 12;
    }

    return (
      cards.reduce(
        (total, card) => total + getCardHeight(card),
        0
      ) +
      Math.max(0, cards.length - 1) * CARD_GAP +
      4
    );
  };

  const maxDayHeight = Math.max(
    18,
    ...shiftsByDay.map(getDayHeight)
  );

  // --------------------------------------------------
  // DIBUJAR CADA RECUADRO
  // --------------------------------------------------

  const drawShiftCard = (
    card: ShiftCardData,
    x: number,
    y: number,
    customHeight?: number
  ) => {
    const color = COLORS[card.type];
    const height = customHeight ?? getCardHeight(card);

    doc.setFillColor(...color.background);
    doc.setDrawColor(...color.border);
    doc.setLineWidth(0.25);

    doc.roundedRect(
      x,
      y,
      cardWidth,
      height,
      CARD_RADIUS,
      CARD_RADIUS,
      'FD'
    );

    const textX = x + CARD_PADDING;
    let textY = y + CARD_PADDING + 2.5;

    // Etiqueta
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(...color.text);

    doc.text(
      color.label,
      textX,
      textY
    );

    textY += 4;

    // Horario destacado
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);

    const timeLines = doc.splitTextToSize(
      card.time,
      textWidth
    );

    doc.text(
      timeLines,
      textX,
      textY
    );

    textY += timeLines.length * 3.7 + 1;

    // Nombre del Spot
    if (card.spot) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(35, 45, 60);

      const lines = doc.splitTextToSize(
        card.spot,
        textWidth
      );

      doc.text(
        lines,
        textX,
        textY
      );

      textY += lines.length * 3 + 1;
    }

    // Dirección y ciudad
    if (card.location) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(65, 75, 90);

      const lines = doc.splitTextToSize(
        card.location,
        textWidth
      );

      doc.text(
        lines,
        textX,
        textY
      );

      textY += lines.length * 2.8 + 1;
    }

    // Observaciones
    if (card.observations) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(65, 75, 90);

      const lines = doc.splitTextToSize(
        String(card.observations),
        textWidth
      );

      doc.text(
        lines,
        textX,
        textY
      );
    }

    return height;
  };

  // --------------------------------------------------
  // ENCABEZADO CORPORATIVO
  // --------------------------------------------------

  await createCorporatePage(
    doc,
    'PROGRAMACIÓN SEMANAL'
  );

  let y = 50;

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(35, 45, 60);
  doc.setFontSize(11);

  doc.text(
    `Empleado: ${employee.name}`,
    14,
    y
  );

  y += 7;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);

  doc.text(
    `Semana: ${startOfWeek.format('DD/MM/YYYY')} - ${endOfWeek.format('DD/MM/YYYY')}`,
    14,
    y
  );

  y += 8;

  // --------------------------------------------------
  // TABLA SEMANAL
  // --------------------------------------------------

  const tableHead = [
    ['Empleado', ...days.map(day => day.format('ddd DD/MM'))]
  ];

  // El texto de las celdas se dibuja manualmente.
  const row = [
    employee.name,
    ...days.map(() => '')
  ];

  autoTable(doc, {
    head: tableHead,
    body: [row],
    startY: y,

    margin: {
      left: PAGE_MARGIN,
      right: PAGE_MARGIN,
      bottom: 18,
    },

    tableWidth: pageWidth - PAGE_MARGIN * 2,

    styles: {
      fontSize: 8,
      cellPadding: 1.5,
      valign: 'top',
      overflow: 'linebreak',
      lineColor: [220, 225, 230],
      lineWidth: 0.2,
    },

    headStyles: {
      fillColor: [64, 132, 199],
      textColor: 255,
      halign: 'center',
      fontStyle: 'bold',
    },

    bodyStyles: {
      minCellHeight: maxDayHeight + 3,
      fillColor: [255, 255, 255],
    },

    columnStyles: {
      0: {
        cellWidth: FIRST_COLUMN_WIDTH,
        fontStyle: 'bold',
      },

      1: { cellWidth: dayColumnWidth },
      2: { cellWidth: dayColumnWidth },
      3: { cellWidth: dayColumnWidth },
      4: { cellWidth: dayColumnWidth },
      5: { cellWidth: dayColumnWidth },
      6: { cellWidth: dayColumnWidth },
      7: { cellWidth: dayColumnWidth },
    },

    didParseCell: data => {
      if (data.column.index === 0) {
        return;
      }

      const day = days[data.column.index - 1];

      if (isHolidayOrSunday(day, holidays)) {
        if (data.section === 'head') {
          data.cell.styles.fillColor = [192, 57, 43];
          data.cell.styles.textColor = 255;
          data.cell.styles.fontStyle = 'bold';
        }

        if (data.section === 'body') {
          data.cell.styles.fillColor = [255, 245, 245];
        }
      }
    },

    didDrawCell: data => {
      if (
        data.section !== 'body' ||
        data.column.index === 0
      ) {
        return;
      }

      const dayIndex = data.column.index - 1;
      const cards = shiftsByDay[dayIndex];

      const cellX = data.cell.x;
      const cellY = data.cell.y;

      if (cards.length === 0) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(150, 150, 150);

        doc.text(
          '—',
          cellX + data.cell.width / 2,
          cellY + 9,
          { align: 'center' }
        );

        return;
      }

      let cardY = cellY + 1.5;
      const cardX = cellX + 1.5;

      // Altura disponible dentro de la celda
      const availableHeight = data.cell.height - 3;

      // Identificar si este día tiene una única jornada completa
      const isSingleFullDay =
        cards.length === 1 &&
        cards[0].type === 'full';

      cards.forEach(card => {

        // Si es jornada completa y es el único turno del día,
      // ocupar toda la altura disponible de la celda.
        const customHeight = isSingleFullDay
          ? Math.max(getCardHeight(card), availableHeight)
          : undefined;

        const height = drawShiftCard(
          card,
          cardX,
          cardY,
          customHeight
        );

        cardY += height + CARD_GAP;
      });
    },

    didDrawPage: () => {
      drawFooter(doc);
    },
  });

  // --------------------------------------------------
  // GUARDAR PDF
  // --------------------------------------------------

  const safeName = employee.name
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '_');

  doc.save(
    `empleado_${safeName}_semana_${weekLabel}.pdf`
  );
};


export const exportSingleSpotPDF = async (
  spot: Spot,
  shifts: Shift[],
  weekStart: Date,
  holidays: Date[]
): Promise<void> => {
  try {
    const generatedPDF = await createSingleSpotPDF(
      spot,
      shifts,
      weekStart,
      holidays
    );

    generatedPDF.doc.save(generatedPDF.fileName);
  } catch (error) {
    console.error(
      'Error exportando el PDF individual del puesto:',
      error
    );

    throw error;
  }
};

export const createSingleSpotPDF = async (
  spot: Spot,
  shifts: Shift[],
  weekStart: Date,
  holidays: Date[]
): Promise<GeneratedSpotPDF> => {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  const startOfWeek = moment(weekStart).startOf('week');
  const endOfWeek = startOfWeek.clone().endOf('week');

  const weekLabel =
    `${startOfWeek.format('YYYY-MM-DD')}_a_${endOfWeek.format('YYYY-MM-DD')}`;

  const days = Array.from({ length: 7 }).map((_, index) =>
    startOfWeek.clone().add(index, 'days')
  );

  const tableHead = [
    ['Cliente', ...days.map((day) => day.format('ddd DD/MM'))],
  ];

  const row: string[] = [spot.name];

  days.forEach((day) => {
    const shiftsForDay = shifts
      .filter((shift) =>
        moment(shift.startDateTime).isSame(day, 'day')
      )
      .sort((a, b) =>
        moment(a.startDateTime).diff(moment(b.startDateTime))
      );

    if (shiftsForDay.length === 0) {
      row.push('—');
      return;
    }

    const cellContent = shiftsForDay
      .map((shift) => {
        const employee =
          shift.employee?.name || 'Sin asignar';

        const time =
          `${moment(shift.startDateTime).format('hh:mm A')} - ` +
          `${moment(shift.endDateTime).format('hh:mm A')}`;

        return `${employee}\n${time}`;
      })
      .join('\n\n');

    row.push(cellContent);
  });

  await createCorporatePage(
    doc,
    'PROGRAMACIÓN SEMANAL'
  );

  const location = [
    spot.address,
    spot.city,
  ]
    .filter(Boolean)
    .join(' ');

  const tableStartY = drawSpotInformation(
    doc,
    spot.name,
    `${startOfWeek.format('DD/MM/YYYY')} - ${endOfWeek.format('DD/MM/YYYY')}`,
    location,
    'Confirmado'
  );

  autoTable(doc, {
    head: tableHead,
    body: [row],
    startY: tableStartY,

    styles: {
      fontSize: 8,
      cellPadding: 3,
      valign: 'top',
      overflow: 'linebreak',
    },

    headStyles: {
      fillColor: [64, 132, 199],
      textColor: 255,
      halign: 'center',
    },

    columnStyles: {
      0: {
        cellWidth: 45,
        fontStyle: 'bold',
      },
    },

    didDrawPage: () => {
      drawFooter(doc);
    },

    didParseCell: (data) => {
      if (data.column.index === 0) {
        return;
      }

      const day = days[data.column.index - 1];

      if (!isHolidayOrSunday(day, holidays)) {
        return;
      }

      if (data.section === 'head') {
        data.cell.styles.fillColor = [192, 57, 43];
        data.cell.styles.textColor = 255;
        data.cell.styles.fontStyle = 'bold';
      }

      if (data.section === 'body') {
        data.cell.styles.fillColor = [255, 235, 238];
      }
    },
  });

  let y = (doc as any).lastAutoTable.finalY + 8;

  y = drawObservations(doc, y);
  y = drawThankYouMessage(doc, y + 6);

  drawFooter(doc);

  const safeName = spot.name
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '_');

  return {
    doc,
    fileName: `Cliente_${safeName}_semana_${weekLabel}.pdf`,
  };
};

export const exportIndividualSpotPDFsZip = async (
  spots: Spot[],
  spotIdToShiftListMap: Map<number, Shift[]>,
  weekStart: Date,
  holidays: Date[]
): Promise<void> => {

  const zip = new JSZip();

  const startOfWeek = moment(weekStart).startOf('week');
  const endOfWeek = startOfWeek.clone().endOf('week');

  const weekLabel =
    `${startOfWeek.format('YYYY-MM-DD')}_a_${endOfWeek.format('YYYY-MM-DD')}`;

  let generatedFiles = 0;

  for (const spot of spots) {

    if (spot.id == null) {
      continue;
    }

    const shifts =
      spotIdToShiftListMap.get(spot.id) ?? [];

    // 🔥 Si no tiene turnos, no generar PDF
    if (shifts.length === 0) {
      continue;
    }

    generatedFiles++;

    const generatedPDF = await createSingleSpotPDF(
      spot,
      shifts,
      weekStart,
      holidays
    );

    const pdfBlob = generatedPDF.doc.output('blob');

    zip.file(
      generatedPDF.fileName,
      pdfBlob
    );
  }

  // 🔥 Si ningún puesto tenía turnos
  if (generatedFiles === 0) {
    alert("No existen clientes con turnos programados para exportar.");
    return;
  }

  const zipBlob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: {
      level: 6,
    },
  });

  saveAs(
    zipBlob,
    `programacion_clientes_semana_${weekLabel}.zip`
  );
};